<template>
  <div class="page-renderer" :class="{ 'page-renderer--editing': editMode }">
    <page-node-children
      :page-id="pageId"
      :parent-id="null"
      :edit-mode="editMode"
    />
  </div>
</template>

<script>
import PageNodeChildren from './PageNodeChildren.vue';

export default {
  name: 'PageRenderer',
  components: { PageNodeChildren },
  props: {
    pageId: { type: String, required: true },
    editMode: { type: Boolean, default: false },
  },
};
</script>

<style lang="less">
.page-renderer {
  min-height: 100px;

  &--editing {
    // Only show outline around draggable items
    .page-node-wrapper--draggable {
      outline: 1px dashed var(--v-darkGray-base);
      outline-offset: 2px;

      &:hover {
        outline-color: var(--v-appMain-base);
      }
    }
  }

  // ============================================
  // TYPOGRAPHY SYSTEM
  // Inspired by Notion, Medium, and ChatGPT
  // Focused on scannability and hierarchy
  // ============================================

  // Base text settings
  font-size: 15px;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.87);

  // Headings - using ~1.25 modular scale
  // Margin-top creates separation, margin-bottom is minimal
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0;
    font-weight: 600;
    line-height: 1.3;
    color: rgba(0, 0, 0, 0.9);
    letter-spacing: -0.01em;
  }

  h1 {
    font-size: 1.875rem; // 28px at 15px base
    font-weight: 700;
    line-height: 1.2;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
    letter-spacing: -0.02em;

    &:first-child {
      margin-top: 0;
    }
  }

  h2 {
    font-size: 1.5rem; // 22.5px
    font-weight: 600;
    margin-top: 1.75rem;
    margin-bottom: 0.5rem;

    &:first-child {
      margin-top: 0;
    }
  }

  h3 {
    font-size: 1.25rem; // 18.75px
    font-weight: 600;
    margin-top: 1.5rem;
    margin-bottom: 0.375rem;

    &:first-child {
      margin-top: 0;
    }
  }

  h4 {
    font-size: 1.1rem; // 16.5px
    font-weight: 600;
    margin-top: 1.25rem;
    margin-bottom: 0.25rem;

    &:first-child {
      margin-top: 0;
    }
  }

  h5 {
    font-size: 1rem; // 15px
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: rgba(0, 0, 0, 0.7);
    margin-top: 1.25rem;
    margin-bottom: 0.25rem;

    &:first-child {
      margin-top: 0;
    }
  }

  h6 {
    font-size: 0.875rem; // 13px
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(0, 0, 0, 0.6);
    margin-top: 1rem;
    margin-bottom: 0.25rem;

    &:first-child {
      margin-top: 0;
    }
  }

  // Paragraphs
  p {
    margin: 0 0 0.75rem 0;
    line-height: 1.6;

    &:last-child {
      margin-bottom: 0;
    }
  }

  // Adjacent heading + paragraph tightening
  h1 + p,
  h2 + p,
  h3 + p,
  h4 + p,
  h5 + p,
  h6 + p {
    margin-top: 0;
  }

  // Strong / Bold
  strong,
  b {
    font-weight: 600;
    color: rgba(0, 0, 0, 0.9);
  }

  // Emphasis / Italic
  em,
  i {
    font-style: italic;
  }

  // Links
  a {
    color: var(--v-primary-base, #1976d2);
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: border-color 0.15s ease;

    &:hover {
      border-bottom-color: currentColor;
    }
  }

  // Lists
  ul,
  ol {
    margin: 0 0 0.75rem 0;
    padding-left: 1.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  li {
    margin-bottom: 0.25rem;
    line-height: 1.5;

    &:last-child {
      margin-bottom: 0;
    }

    // Nested lists
    ul,
    ol {
      margin-top: 0.25rem;
      margin-bottom: 0.25rem;
    }
  }

  // Unordered list styling
  ul {
    list-style-type: disc;

    ul {
      list-style-type: circle;

      ul {
        list-style-type: square;
      }
    }
  }

  // Ordered list styling
  ol {
    list-style-type: decimal;

    ol {
      list-style-type: lower-alpha;

      ol {
        list-style-type: lower-roman;
      }
    }
  }

  // Blockquotes
  blockquote {
    margin: 1rem 0;
    padding: 0.5rem 0 0.5rem 1rem;
    border-left: 3px solid rgba(0, 0, 0, 0.12);
    color: rgba(0, 0, 0, 0.7);
    font-style: italic;

    p {
      margin-bottom: 0.5rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  // Code - inline
  code {
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
    font-size: 0.875em;
    padding: 0.15em 0.4em;
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
    color: rgba(0, 0, 0, 0.8);
  }

  // Code blocks
  pre {
    margin: 1rem 0;
    padding: 1rem;
    background-color: rgba(0, 0, 0, 0.03);
    border-radius: 6px;
    overflow-x: auto;
    font-size: 0.875rem;
    line-height: 1.5;

    code {
      padding: 0;
      background: none;
      border-radius: 0;
      font-size: inherit;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  // Horizontal rule
  hr {
    margin: 1.5rem 0;
    border: none;
    height: 1px;
    background-color: rgba(0, 0, 0, 0.1);
  }

  // Tables
  table {
    width: 100%;
    margin: 1rem 0;
    border-collapse: collapse;
    font-size: 0.9375rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  th,
  td {
    padding: 0.5rem 0.75rem;
    text-align: left;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  th {
    font-weight: 600;
    color: rgba(0, 0, 0, 0.7);
    font-size: 0.8125rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  tbody tr:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }

  // Images
  // img {
  //   max-width: 100%;
  //   height: auto;
  //   border-radius: 4px;
  // }

  // Small text / captions
  small {
    font-size: 0.8125rem;
    color: rgba(0, 0, 0, 0.6);
  }

  // Mark / Highlight
  mark {
    background-color: rgba(255, 235, 59, 0.4);
    padding: 0.1em 0.2em;
    border-radius: 2px;
  }
}
</style>
